import { Badge, Banner, Button, Checkbox, Dialog, Field, Input, InputArea, Select, Switch } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../../components/ui/FormPage';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DangerButton } from '../../components/ui/DangerButton';
import { ICONS } from '../../components/ui/icons';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { BrandLogo } from '../../components/BrandLogo';
import { InstagramStatusBadge } from '../../components/connections/InstagramAccounts';
import { useInstagramConnect } from '../../hooks/useInstagramConnect';
import { SkeletonText } from '../../components/ui/Skeleton';

interface Account {
  id: string; username: string; name: string | null; profilePictureUrl: string | null; status: string;
  tokenExpiresAt: string | null; tokenRefreshError: string | null; iceBreakers: Array<{ question: string; reply: string }>;
}
interface Automation {
  id: string; accountId: string; name: string; trigger: 'comment' | 'story_reply'; active: boolean;
  postIds: string[]; keywordMode: 'any' | 'contains' | 'equals'; keywords: string[]; excludeKeywords: string[];
  publicReplies: string[]; privateReply: string | null; hideComment: boolean; oncePerUser: boolean; ignoreReplies: boolean;
  delaySeconds: number; repliesCount: number;
}
type Draft = Omit<Automation, 'id' | 'repliesCount'> & { id?: string };

const emptyDraft = (accountId: string): Draft => ({
  accountId, name: '', trigger: 'comment', active: true, postIds: [], keywordMode: 'contains', keywords: [], excludeKeywords: [],
  publicReplies: ['Te chamei no direct! 📩'], privateReply: 'Oi {{usuario}}! Aqui está o que você pediu:', hideComment: false,
  oncePerUser: true, ignoreReplies: true, delaySeconds: 10,
});
const lines = (v: string) => v.split('\n').map((x) => x.trim()).filter(Boolean);
const commas = (v: string) => v.split(',').map((x) => x.trim()).filter(Boolean);

const TRIGGER_OPTIONS = [
  { value: 'comment', label: 'Alguém comentar num post' },
  { value: 'story_reply', label: 'Alguém responder um story' },
];
const KEYWORD_MODE_OPTIONS = [
  { value: 'any', label: 'Qualquer texto' },
  { value: 'contains', label: 'Contém uma das palavras' },
  { value: 'equals', label: 'É exatamente uma das palavras' },
];

/** Ferramentas › Instagram */
export function InstagramPage() {
  const { can } = useTenant();
  const canConnect = can('connections:manage');
  const canEdit = can('campaigns:write');
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const connect = useInstagramConnect();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<{ configured: boolean; accounts: Account[]; automations: Automation[] } | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [breakers, setBreakers] = useState<{ account: Account; items: Array<{ question: string; reply: string }> } | null>(null);
  const [disconnecting, setDisconnecting] = useState<Account | null>(null);
  const [deleting, setDeleting] = useState<Automation | null>(null);

  const load = useCallback(async () => {
    const res = await fetchApi('/api/tools/instagram');
    if (res.ok) setData(await res.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  // Volta do login do Instagram
  useEffect(() => {
    const ok = params.get('conectado');
    const err = params.get('erro');
    if (ok) toast.success(`@${ok} conectado`);
    if (err) toast.error(err);
    if (ok || err) setParams({}, { replace: true });
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps


  const call = async (url: string, init: RequestInit, okMsg: string) => {
    const res = await fetchApi(url, init);
    const j = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(j.message || 'Não foi possível salvar'); return false; }
    toast.success(okMsg);
    load();
    return true;
  };

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    const ok = await call(draft.id ? `/api/tools/instagram/automations/${draft.id}` : '/api/tools/instagram/automations',
      { method: draft.id ? 'PUT' : 'POST', body: JSON.stringify(draft) }, draft.id ? 'Automação salva' : 'Automação criada');
    setSaving(false);
    if (ok) setDraft(null);
  };

  if (!data) return <div className="py-8"><SkeletonText lines={4} /></div>;
  const connected = data.accounts.filter((a) => a.status === 'CONNECTED');
  const accountName = (id: string) => `@${data.accounts.find((a) => a.id === id)?.username ?? '?'}`;
  const accountOptions = connected.map((a) => ({ value: a.id, label: `@${a.username}` }));

  if (draft) {
    return (
      <FormPage
        backTo="/ferramentas/instagram"
        backLabel="Instagram"
        onBack={() => setDraft(null)}
        title={draft?.id ? 'Editar automação' : 'Nova automação'}
        description="Responda comentários e mensagens do Instagram automaticamente."
        submitLabel={'Salvar'}
        submitting={saving}
        submitDisabled={!draft?.name.trim()}
        onSubmit={saveDraft}
        onCancel={() => setDraft(null)}
      >
        <FormSection title="Detalhes">


        {draft && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Nome">
                <Input onChange={(e) => setDraft({ ...draft, name: e.target.value })} value={draft.name} placeholder="Ex.: Link do e-book" />
              </Field>
              <Field label="Conta">
                <Select value={(accountOptions.find((o) => o.value === draft.accountId) ?? null)?.value ?? null} items={accountOptions} onValueChange={(value: string | null) => setDraft({ ...draft, accountId: value ?? '' })} />
              </Field>
              <Field label="Quando">
                <Select value={(TRIGGER_OPTIONS.find((o) => o.value === draft.trigger) ?? null)?.value ?? null} items={TRIGGER_OPTIONS} onValueChange={(value: string | null) => setDraft({ ...draft, trigger: value as Draft['trigger'] })} />
              </Field>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Texto">
                <Select value={(KEYWORD_MODE_OPTIONS.find((o) => o.value === draft.keywordMode) ?? null)?.value ?? null} items={KEYWORD_MODE_OPTIONS} onValueChange={(value: string | null) => setDraft({ ...draft, keywordMode: value as Draft['keywordMode'] })} />
              </Field>
              <Field label="Palavras-chave (separadas por vírgula)">
                <Input onChange={(e) => setDraft({ ...draft, keywords: commas(e.target.value) })} disabled={draft.keywordMode === 'any'} value={draft.keywords.join(', ')} placeholder="eu quero, link" />
              </Field>
              <Field label="Ignorar se tiver (vírgula)">
                <Input onChange={(e) => setDraft({ ...draft, excludeKeywords: commas(e.target.value) })} value={draft.excludeKeywords.join(', ')} />
              </Field>
            </div>
            <Field label={draft.trigger === 'comment' ? 'Só nestes posts (ids, vírgula; vazio = todos)' : 'Só nestes stories (ids, vírgula; vazio = todos)'}>
              <Input onChange={(e) => setDraft({ ...draft, postIds: commas(e.target.value) })} value={draft.postIds.join(', ')} />
            </Field>
            {draft.trigger === 'comment' && (
              <InputArea label="Respostas públicas no post (uma por linha, sorteia uma; vazio = não responde)" onChange={(e) => setDraft({ ...draft, publicReplies: lines(e.target.value) })} rows={3} value={draft.publicReplies.join('\n')} />
            )}
            <InputArea label="Mensagem por DM" description="Use {{usuario}} para o @ da pessoa" onChange={(e) => setDraft({ ...draft, privateReply: e.target.value })} rows={3} value={draft.privateReply || ''} />
            <div className="flex flex-wrap gap-6">
              {draft.trigger === 'comment' && (
                <Checkbox onCheckedChange={(checked: boolean) => setDraft({ ...draft, hideComment: checked })} checked={draft.hideComment} label={<>
                  Ocultar o comentário
                </>} />
              )}
              <Checkbox onCheckedChange={(checked: boolean) => setDraft({ ...draft, oncePerUser: checked })} checked={draft.oncePerUser} label={<>
                Uma vez por pessoa em cada post
              </>} />
              {draft.trigger === 'comment' && (
                <Checkbox onCheckedChange={(checked: boolean) => setDraft({ ...draft, ignoreReplies: checked })} checked={draft.ignoreReplies} label={<>
                  Ignorar respostas a comentários
                </>} />
              )}
            </div>
            {draft.trigger === 'comment' && (
              <div style={{ maxWidth: 260 }}>
                <Field label="Esperar antes de responder (segundos)">
                  <Input onChange={(e) => setDraft({ ...draft, delaySeconds: Number(e.target.value) || 0 })} type="number" value={String(draft.delaySeconds)} />
                </Field>
              </div>
            )}
            </div>
)}
        </FormSection>
      </FormPage>
    );
  }

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <Link to="/ferramentas" className="tools-back"><ICONS.ArrowLeft size={16} /> Ferramentas</Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3" style={{ marginBottom: 4 }}>
              <BrandLogo brand="instagram" size={32} rounded={8} />
              <h2 className="text-xl font-semibold" style={{ fontSize: 24, fontWeight: 600 }}>Instagram</h2>
            </div>
            <div style={{ color: 'var(--text-muted)' }}>
              Conecte a conta profissional para responder DMs com as <Link to="/respostas-automaticas">respostas automáticas</Link> e
              transformar comentários e respostas a stories em conversa. O Instagram só permite mensagem para quem falou com você nas últimas 24 h.
            </div>
          </div>
          {canConnect && data.configured && (
            <Button variant="primary" onClick={connect}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <BrandLogo brand="instagram" size={16} rounded={4} /> Conectar Instagram
              </span>
            </Button>
          )}
        </div>
      </div>

      <div className="page-content space-y-6">
        {!data.configured && <Banner variant="alert" description={<>O app do Instagram ainda não foi configurado pelo administrador da plataforma (Plataforma › Integrações › App da Meta).</>} />}

        <section className="space-y-3">
          <h4 className="text-base font-semibold">Contas</h4>
          {data.accounts.length === 0 && <Banner variant="default" description={<>Nenhuma conta conectada. Use uma conta Empresa ou Criador de conteúdo.</>} />}
          {data.accounts.map((a) => (
            <div key={a.id} className="brand-card flex items-center justify-between gap-4">
              <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
                {a.profilePictureUrl ? <img src={a.profilePictureUrl} alt="" width={40} height={40} style={{ borderRadius: 20 }} /> : <BrandLogo brand="instagram" size={40} rounded={20} />}
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-semibold">@{a.username}</div>
                    <InstagramStatusBadge status={a.status} />
                  </div>
                  <div className="text-xs !text-[var(--text-muted)]">
                    {a.name || ''}{a.tokenExpiresAt ? ` · acesso renovado até ${new Date(a.tokenExpiresAt).toLocaleDateString('pt-BR')}` : ''}
                    {a.tokenRefreshError ? ` · ${a.tokenRefreshError}` : ''}
                  </div>
                </div>
              </div>
              <div className="flex gap-2" style={{ flexShrink: 0 }}>
                {a.status === 'CONNECTED' && canEdit && (
                  <Button variant="secondary" onClick={() => setBreakers({ account: a, items: a.iceBreakers.length ? a.iceBreakers : [{ question: '', reply: '' }] })}>Perguntas prontas</Button>
                )}
                {a.status !== 'CONNECTED' && canConnect && data.configured && <Button variant="secondary" onClick={connect}>Reconectar</Button>}
                {a.status === 'CONNECTED' && canConnect && <DangerButton variant="dangerOutline" onClick={() => setDisconnecting(a)}>Desconectar</DangerButton>}
              </div>
            </div>
          ))}
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-base font-semibold">Automações</h4>
            {canEdit && connected.length > 0 && <Button variant="secondary" icon={ICONS.Plus} onClick={() => setDraft(emptyDraft(connected[0].id))}>Nova automação</Button>}
          </div>
          {data.automations.length === 0 && <div style={{ color: 'var(--text-muted)' }}>Ex.: quem comentar "EU QUERO" no post recebe o link por DM e uma resposta pública.</div>}
          {data.automations.map((a) => (
            <div key={a.id} className="brand-card flex items-center justify-between gap-4">
              <div style={{ minWidth: 0 }}>
                <div className="flex items-center gap-2">
                  <div className="font-semibold">{a.name}</div>
                  <Badge variant="neutral">{a.trigger === 'comment' ? 'Comentário' : 'Resposta a story'}</Badge>
                </div>
                <div className="text-xs !text-[var(--text-muted)]">
                  {accountName(a.accountId)} · {a.keywordMode === 'any' ? 'qualquer texto' : `${a.keywordMode === 'equals' ? 'igual a' : 'contém'} ${a.keywords.join(', ')}`}
                  {' · '}{a.postIds.length ? `${a.postIds.length} post(s)` : 'todos os posts'} · {a.repliesCount} respondidos
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                  <Switch onCheckedChange={(checked: boolean) => call(`/api/tools/instagram/automations/${a.id}`, { method: 'PUT', body: JSON.stringify({ active: checked }) }, checked ? 'Automação ligada' : 'Automação desligada')} aria-label={`Ativar ${a.name}`} checked={a.active} />
                  <Button variant="ghost" shape="square" icon={ICONS.Edit} aria-label={`Editar ${a.name}`} onClick={() => setDraft({ ...a })} />
                  <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label={`Excluir ${a.name}`} onClick={() => setDeleting(a)} />
                </div>
              )}
            </div>
          ))}
        </section>
      </div>



      <Dialog.Root open={!!breakers} onOpenChange={(o: boolean) => { if (!o) (() => { if (breakers) setBreakers(null); })(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Perguntas prontas de @{breakers?.account.username}</h3>}</Dialog.Title>

        {breakers && (
          <div className="space-y-4">
            <div style={{ color: 'var(--text-muted)' }}>Aparecem para quem abre a conversa pela primeira vez (até 4). Ao tocar, a pessoa recebe a resposta.</div>
            {breakers.items.map((it, i) => (
              <div key={i} className="grid gap-2 md:grid-cols-2">
                <Field label={`Pergunta ${i + 1}`}>
                  <Input onChange={(e) => setBreakers({ ...breakers, items: breakers.items.map((x, j) => (j === i ? { ...x, question: e.target.value } : x)) })} value={it.question} />
                </Field>
                <Field label="Resposta">
                  <Input onChange={(e) => setBreakers({ ...breakers, items: breakers.items.map((x, j) => (j === i ? { ...x, reply: e.target.value } : x)) })} value={it.reply} />
                </Field>
              </div>
            ))}
            {breakers.items.length < 4 && <Button variant="secondary" onClick={() => setBreakers({ ...breakers, items: [...breakers.items, { question: '', reply: '' }] })}>Adicionar pergunta</Button>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setBreakers(null)}>Cancelar</Button>
              <Button variant="primary" onClick={async () => {
                if (await call(`/api/tools/instagram/${breakers.account.id}/ice-breakers`, { method: 'PUT', body: JSON.stringify({ items: breakers.items }) }, 'Perguntas salvas no Instagram')) setBreakers(null);
              }}>Salvar no Instagram</Button>
            </div>
          </div>
        )}
      
</Dialog>
</Dialog.Root>

      <DangerConfirmModal open={!!disconnecting} title="Desconectar Instagram" confirmText="Desconectar" objectName={disconnecting?.username}
        onCancel={() => setDisconnecting(null)} onConfirm={async () => { if (disconnecting) await call(`/api/tools/instagram/${disconnecting.id}`, { method: 'DELETE' }, 'Conta desconectada'); setDisconnecting(null); }}>
        A plataforma para de receber as mensagens e comentários de <strong>@{disconnecting?.username}</strong>. As automações ficam guardadas para quando reconectar.
      </DangerConfirmModal>
      <DangerConfirmModal open={!!deleting} title="Excluir automação" confirmText="Excluir" objectName={deleting?.name}
        onCancel={() => setDeleting(null)} onConfirm={async () => { if (deleting) await call(`/api/tools/instagram/automations/${deleting.id}`, { method: 'DELETE' }, 'Automação excluída'); setDeleting(null); }}>
        A automação <strong>{deleting?.name}</strong> é excluída. Não dá para desfazer.
      </DangerConfirmModal>
    </>
  );
}
